import React, { useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Menu,
  Button,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Context } from "../context";
import { ADMIN } from "../config";

const TopNav = () => {
  const { setLoggedIn, user } = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Determine active page based on current path
  const currentPath = location.pathname;
  const isActive = (path) => currentPath === path;

  // Navigation items configuration
  const navItems = [
    { label: "Patients", path: "/", visible: true },
    { label: "Appointments", path: "/appointments", visible: true },
    { label: "Users", path: "/users", visible: user.role === ADMIN },
    { label: "Billing", path: "/billing", visible: true },
  ];

  // Menu handlers
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Navigation handler
  const navigateTo = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Auth handlers
  const handleLogout = () => {
    window.localStorage.removeItem("alex-med-token");
    setLoggedIn(false);
    navigate("/login");
    handleCloseUserMenu();
  };

  const handleChangePassword = () => {
    navigate("/change-password");
    handleCloseUserMenu();
  };

  // Styles
  const navButtonStyle = {
    height: "65px",
    fontSize: { xs: "16px", md: "18px", lg: "20px" },
    borderRadius: "0",
    px: 2,
    transition: "background-color 0.3s ease",
  };

  const activeNavButtonStyle = {
    ...navButtonStyle,
    background: "#1565c0",
    fontWeight: "bold",
    borderBottom: "3px solid white",
  };

  // Mobile drawer content
  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="h6" component="div">
          {user.email}
        </Typography>
      </Box>
      <Typography sx={{ px: 2, pb: 1 }} variant="body2" color="text.secondary">
        Role: {user.role}
      </Typography>
      <Divider />
      <List>
        {navItems.filter(item => item.visible).map((item) => (
          <ListItem 
            button 
            key={item.path} 
            onClick={() => navigateTo(item.path)}
            selected={isActive(item.path)}
            sx={{
              "&.Mui-selected": {
                backgroundColor: "#e3f2fd",
                borderLeft: "4px solid #1565c0",
              }
            }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem button onClick={handleChangePassword}>
          <ListItemText primary="Change Password" />
        </ListItem>
        <ListItem button onClick={handleLogout}>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar position="static" elevation={3}>
      <Toolbar sx={{ padding: { xs: "0 16px", md: "0 30px" } }}>
        {/* Mobile menu icon */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { md: "none" } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Brand/Logo could go here */}
        <Typography
          variant="h6"
          component="div"
          sx={{ display: { xs: "flex", md: "none" }, flexGrow: 1 }}
        >
          MedApp
        </Typography>

        {/* Desktop navigation */}
        <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
          {navItems.map(
            (item) =>
              item.visible && (
                <Button
                  key={item.path}
                  color="inherit"
                  sx={isActive(item.path) ? activeNavButtonStyle : navButtonStyle}
                  onClick={() => navigateTo(item.path)}
                >
                  {item.label}
                </Button>
              )
          )}
        </Box>

        {/* User menu */}
        <Box sx={{ flexGrow: 0 }}>
          <Tooltip title="Account settings">
            <Button
              onClick={handleOpenUserMenu}
              sx={{ 
                color: "white", 
                textTransform: "none",
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                }
              }}
              startIcon={<AccountCircleIcon />}
            >
              <Box sx={{ textAlign: "left", display: { xs: "none", sm: "block" } }}>
                <Typography variant="body2" component="div">
                  {user.email}
                </Typography>
                <Typography variant="caption" component="div">
                  {user.role}
                </Typography>
              </Box>
            </Button>
          </Tooltip>
          <Menu
            sx={{ mt: "45px" }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            <MenuItem onClick={handleChangePassword}>
              <Typography textAlign="center">Change Password</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Typography textAlign="center">Logout</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default TopNav;